import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable';

import NavBarThree from "../NavBarThree";
import ListVertical from "../ListVertical";
import { CSS } from '@dnd-kit/utilities';
import { MenuOutlined } from "@ant-design/icons";
import './style.scss';

interface SortableInterface {
  ids: any[];
  items: any;
  titleKey: string,
  imageKey?: string,
  className?: string;
  onClickSave: Function;
}

interface ItemInterface {
  id: any;
  children: React.ReactNode;
}

function Sortable({ ids, items, imageKey, titleKey, onClickSave, className }: SortableInterface) {
  const [sortedIds, setSortedIds] = useState(ids);

  useEffect(() => {
    setSortedIds(ids);
  }, [ids]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: any) {
    const { active, over } = event;

    // `over` is null when the pointer is released outside the list — a
    // plain click on an item does exactly that.
    if (over && active.id !== over.id) {
      setSortedIds((sortedIds) => {
        const oldIndex = sortedIds.indexOf(active.id);
        const newIndex = sortedIds.indexOf(over.id);

        return arrayMove(sortedIds, oldIndex, newIndex);
      });
    }
  }

  function onClickReset() {
    setSortedIds(ids);
  }

  function onClickSaveBtn() {
    onClickSave(sortedIds);
  }

  const renderItem = (id: any) => {
    const item = items[id];

    if (item) {
      const image = imageKey ? item[imageKey] : undefined;
      const title = titleKey ? item[titleKey] : undefined;

      return(
        <Sortable.Item key={id} id={id} >
          <ListVertical.Item image={image}>
            <span style={item.style}>{title}</span>
          </ListVertical.Item>
        </Sortable.Item>
      )
    }
  }

  return (
    <div className={`v-sortable ${className || ''}`}>
      <NavBarThree className='v-mb-5' align='right'>
        <NavBarThree.Button onClick={onClickReset}>Reset</NavBarThree.Button>
        <NavBarThree.Button onClick={onClickSaveBtn}>Save</NavBarThree.Button>
      </NavBarThree>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortedIds}>
          <ListVertical>
            {sortedIds.map(renderItem)}
          </ListVertical>
        </SortableContext>
      </DndContext>
    </div>
  )
}

Sortable.Item = function Item({ id, children }: ItemInterface) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div ref={setNodeRef} className='v-sortable-item' style={style} {...attributes} {...listeners}>
      {children}

      <MenuOutlined className='o-sort-icon' />
    </div>
  );
}

export default Sortable;
